// NPM packages
import React from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';

// All other imports
import Link from 'components/core/Link';
import LimitedResourceCategoryThumbnail from './LimitedResourceCategoryThumbnail';
import { LIMITED_RESOURCE_CATEGORY_MAX_HEIGHT as MAX_HEIGHT } from './utils';
import { ResourceCategoryStoryblok } from 'types/storyblokTypes';
import TextTruncate from 'react-text-truncate';
import FullWidthTypography from 'components/core/FullWidthTypography';

const ThumbnailWrapper = styled.div(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexShrink: 0,
}));

const Root = styled.div<{ flipped: boolean | undefined; numImages: number }>(
  ({ theme, flipped, numImages }) => {
    return `
      min-width: ${theme.maxToolWidth};
      height: ${MAX_HEIGHT}px;
      display: grid;
      align-items: center;
      grid-template-columns: ${numImages > 0
        ? flipped
          ? '55rem 32.49rem'
          : '32.49rem 55rem'
        : '55rem'
      };
      margin: 2rem 0;
      gap: 2rem;
      padding: 0 ${theme.typography.pxToRem(87)};

      @media (max-width: 1200px) {
        min-width: 0;
        width: 100%;
        height: auto;
        grid-template-columns: 1fr;
        margin: 1.5rem 0;
        padding: 0 ${theme.typography.pxToRem(43.5)};
        gap: 1.5rem;
      }

      @media (max-width: 600px) {
        margin: 1rem 0;
        padding: 0 1rem;
        gap: 1rem;
      }
    `;
  }
);

const Header = styled(FullWidthTypography)(({ theme }) => ({
  marginTop: '0',
  marginBottom: '0.5rem',
  display: 'flex',
  gap: '1rem',
  [theme.breakpoints.down('md')]: {
    fontSize: theme.typography.pxToRem(28),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.pxToRem(24),
  },
}));

const Description = styled.div(({ theme }) => ({
  marginBottom: '0.5rem',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  [theme.breakpoints.down('md')]: {
    fontSize: theme.typography.pxToRem(16),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.pxToRem(14),
  },
}));

const ContentWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
  justifySelf: 'auto',
});

interface LimitedResourceCategoryProps {
  blok: ResourceCategoryStoryblok;
  flipped?: boolean;
  className?: string;
}

function LimitedResourceCategory(
  props: LimitedResourceCategoryProps
): React.ReactElement | null {
  const { blok, flipped, className } = props;
  const linksToExternalSite = blok.linkedUrl?.includes('http');

  const thumbnail = (
    <ThumbnailWrapper>
      <LimitedResourceCategoryThumbnail
        images={blok.images || []}
        flipped={flipped || false}
      />
    </ThumbnailWrapper>
  );

  return (
    <Root
      flipped={flipped}
      numImages={(blok.images || []).length}
      className={className}
    >
      {flipped ? null : thumbnail}
      <ContentWrapper>
        <Header variant="h2">{blok.title}</Header>
        <Description>
          <TextTruncate
            line={5}
            element={'span'}
            truncateText="..."
            text={blok.description}
          />
        </Description>
        {blok.linkedUrl && (
          <Link to={blok.linkedUrl} openNewTab={linksToExternalSite}>
            Learn More...
          </Link>
        )}
      </ContentWrapper>
      {flipped ? thumbnail : null}
    </Root>
  );
}

export default observer(LimitedResourceCategory);
